// CoverageRing.tsx — azimuth × elevation ring of captured viewpoints. Each
// elevation band is a concentric ring (outer = low, inner = high) split into
// azimuth sectors; a sector fills in once a keyframe was taken from there.
import type { Coverage } from '../capture/coverage';
import { AZIMUTH_BINS } from '../capture/coverage';

function sectorPath(cx: number, cy: number, r0: number, r1: number, a0: number, a1: number): string {
  const p = (r: number, a: number) => `${(cx + r * Math.cos(a)).toFixed(2)},${(cy - r * Math.sin(a)).toFixed(2)}`;
  return `M${p(r1, a0)} A${r1},${r1} 0 0 0 ${p(r1, a1)} L${p(r0, a1)} A${r0},${r0} 0 0 1 ${p(r0, a0)} Z`;
}

export interface CoverageRingProps {
  coverage: Coverage;
  size?: number;
  version: number; // bump to re-render after coverage.add
}

export function CoverageRing({ coverage, size = 96 }: CoverageRingProps) {
  const c = size / 2;
  const bands = coverage.hits.length;
  const ringW = (c - 6) / bands;
  const step = (2 * Math.PI) / AZIMUTH_BINS;
  const pct = Math.round(coverage.fraction() * 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Coverage ${pct}%`}>
      <circle cx={c} cy={c} r={c - 1} fill="rgba(0,0,0,0.55)" />
      {coverage.hits.map((row, band) =>
        row.map((n, bin) => {
          const r1 = c - 4 - band * ringW;
          const r0 = r1 - ringW + 1.5;
          return (
            <path
              key={`${band}-${bin}`}
              d={sectorPath(c, c, r0, r1, bin * step + 0.03, (bin + 1) * step - 0.03)}
              fill={n > 0 ? '#14b8a6' : 'rgba(255,255,255,0.15)'}
              opacity={n > 0 ? Math.min(1, 0.5 + 0.25 * n) : 1}
            />
          );
        }),
      )}
      <text x={c} y={c + 4} textAnchor="middle" fontSize="12" fill="#ffffff">{pct}%</text>
    </svg>
  );
}
